<template>
  <div class="interactive-map-container">
    <div ref="mapContainer" class="map-container">
      <!-- CP-274: leaflet chunk gone (tab from before a deploy): note inside the fixed-height box, no auto reload. -->
      <stale-chunk-note v-if="loadFailed" />
    </div>
    <div v-if="ports.length === 0 && (!itinerarySchedule || itinerarySchedule.length === 0)" class="no-ports-message">
      <i class="fas fa-map"></i>
      <p>Port information not available</p>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch, onBeforeUnmount } from 'vue';
import 'leaflet/dist/leaflet.css';
import { geocodePort } from '@/utils/portGeocoder';
import { whenNearViewport } from '@/utils/nearViewport';
import { handleInPageChunkError } from '@/utils/chunkReload';
import StaleChunkNote from './StaleChunkNote.vue';

// We'll use custom divIcon markers instead of default Leaflet icons

// CP-224: leaflet (~145 KB minified) loads when the first map initialises, not in the entry bundle. Its CSS stays
// a static import above so the cascade order against this component's global styles does not change (no visual
// change); the 500 / 400 / 300 px map box is fixed-height, so the late script moves nothing. One retry on failure.
let leafletPromise = null;
function loadLeaflet(retry = 1) {
  if (!leafletPromise) {
    leafletPromise = import(/* webpackChunkName: "leaflet" */ 'leaflet').then((mod) => mod.default || mod).catch((err) => {
      leafletPromise = null;
      if (retry > 0) return loadLeaflet(retry - 1);
      throw err;
    });
  }
  return leafletPromise;
}

export default {
  name: 'InteractiveMap',
  components: { StaleChunkNote },
  props: {
    ports: {
      type: Array,
      default: () => []
    },
    itinerarySchedule: {
      type: Array,
      default: () => []
    }
  },
  setup(props) {
    const mapContainer = ref(null);
    const loadFailed = ref(false);
    let map = null;
    let markers = [];
    let unmounted = false;
    
    // Create a map of port names to day numbers from itinerary schedule
    const getPortDayMap = () => {
      const portDayMap = new Map();
      if (props.itinerarySchedule && props.itinerarySchedule.length > 0) {
        props.itinerarySchedule.forEach(day => {
          const portName = day.port || day.location;
          if (portName && portName !== 'At Sea' && portName !== 'Fun Day at Sea' && portName !== 'Cruising') {
            // If port already exists, keep the first occurrence (earlier day)
            if (!portDayMap.has(portName)) {
              portDayMap.set(portName, day.day || day.number || null);
            }
          }
        });
      }
      return portDayMap;
    };
    
    // Get unique ports with day information
    const getPortsWithDays = async () => {
      const portDayMap = getPortDayMap();
      const portsWithDays = [];
      
      // Use itinerary schedule if available, otherwise use ports array
      if (props.itinerarySchedule && props.itinerarySchedule.length > 0) {
        const seenPorts = new Set();
        for (const day of props.itinerarySchedule) {
          const portName = day.port || day.location;
          if (portName && 
              portName !== 'At Sea' && 
              portName !== 'Fun Day at Sea' && 
              portName !== 'Cruising' &&
              !seenPorts.has(portName)) {
            seenPorts.add(portName);
            const coords = await geocodePort(portName);
            if (coords) {
              portsWithDays.push({
                name: portName,
                day: day.day || day.number || null,
                coordinates: coords
              });
            }
          }
        }
      } else if (props.ports && props.ports.length > 0) {
        // Fallback to ports array
        for (const portName of props.ports) {
          const coords = await geocodePort(portName);
          if (coords) {
            portsWithDays.push({
              name: portName,
              day: portDayMap.get(portName) || null,
              coordinates: coords
            });
          }
        }
      }
      
      return portsWithDays;
    };
    
    const initializeMap = async () => {
      if (!mapContainer.value) return;
      
      // Clear existing map
      if (map) {
        map.remove();
        markers = [];
      }
      
      // Get ports with coordinates
      const portsWithDays = await getPortsWithDays();
      
      if (portsWithDays.length === 0) {
        mapContainer.value.innerHTML = `
          <div class="no-ports-message">
            <i class="fas fa-map"></i>
            <p>Port information not available</p>
          </div>
        `;
        return;
      }
      
      let L;
      try {
        L = await loadLeaflet();
      } catch (err) {
        // script failed twice: 'refresh to update' note in the fixed-height box (CP-274), never a reload
        if (!unmounted) handleInPageChunkError(err, { showNote: () => { loadFailed.value = true; } });
        return;
      }
      if (unmounted || !mapContainer.value) return;

      // Initialize map
      map = L.map(mapContainer.value, {
        zoomControl: true,
        scrollWheelZoom: true
      });
      
      // Add OpenStreetMap tiles
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 18
      }).addTo(map);
      
      // Calculate bounds to fit all ports
      const bounds = L.latLngBounds(portsWithDays.map(p => p.coordinates));
      map.fitBounds(bounds, { padding: [50, 50] });
      
      // Add markers for each port
      portsWithDays.forEach((port) => {
        // Create popup content with port name and day
        let popupContent = `<strong>${port.name}</strong>`;
        if (port.day) {
          popupContent += `<br><span class="day-label">Day ${port.day}</span>`;
        }
        
        // Create custom marker with day number
        const customIcon = L.divIcon({
          className: 'custom-port-marker',
          html: `
            <div class="marker-wrapper">
              <div class="marker-pin"></div>
              ${port.day ? `<div class="day-badge">Day ${port.day}</div>` : ''}
            </div>
          `,
          iconSize: [40, 50],
          iconAnchor: [20, 50],
          popupAnchor: [0, -50]
        });
        
        const marker = L.marker(port.coordinates, {
          icon: customIcon
        }).addTo(map);
        
        marker.bindPopup(popupContent);
        
        markers.push(marker);
      });
      
      // Draw route line connecting ports
      if (portsWithDays.length > 1) {
        const routeCoordinates = portsWithDays.map(p => p.coordinates);
        L.polyline(routeCoordinates, {
          color: '#1a73e8',
          weight: 3,
          opacity: 0.7,
          smoothFactor: 1
        }).addTo(map);
      }
    };
    
    // CP-224: geocoding, the leaflet chunk and the tiles wait until the map nears the viewport (below the fold on
    // mobile), so none of it competes with the LCP paint. The map box is fixed-height, so nothing moves.
    let nearViewport = false;
    let cancelNear = null;
    onMounted(() => {
      cancelNear = whenNearViewport(mapContainer.value, () => {
        nearViewport = true;
        // Small delay to ensure container is rendered
        setTimeout(() => {
          initializeMap();
        }, 100);
      });
    });
    
    onBeforeUnmount(() => {
      unmounted = true;
      if (cancelNear) cancelNear();
      if (map) {
        map.remove();
        map = null;
        markers = [];
      }
    });
    
    watch(() => [props.ports, props.itinerarySchedule], () => {
      if (!nearViewport) return; // the first initializeMap runs when the map nears the viewport
      initializeMap();
    }, { deep: true });
    
    return {
      mapContainer,
      loadFailed
    };
  }
};
</script>

<style scoped>
.interactive-map-container {
  width: 100%;
  margin: 20px 0;
}

.map-container {
  width: 100%;
  height: 500px;
  background-color: var(--cs-d-tint);
  border-radius: 8px;
  position: relative;
  overflow: hidden;
  z-index: 0;
}

.no-ports-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 500px;
  color: var(--cs-d-muted);
  background-color: var(--cs-d-surface-alt);
  border-radius: 8px;
}

.no-ports-message i {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
}

@media (max-width: 768px) {
  .map-container {
    height: 400px;
  }
}

@media (max-width: 480px) {
  .map-container {
    height: 300px;
  }
}
</style>

<style>
/* Global styles for Leaflet custom markers */
.custom-port-marker {
  background: transparent;
  border: none;
}

.marker-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.marker-pin {
  width: 20px;
  height: 20px;
  background-color: #1a73e8;
  border: 3px solid white;
  border-radius: 50%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  position: relative;
  z-index: 2;
}

.marker-pin::after {
  content: '';
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 8px solid #1a73e8;
}

.day-badge {
  background-color: #1a73e8;
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: bold;
  white-space: nowrap;
  margin-top: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  z-index: 3;
  position: relative;
}

/* Leaflet popup styling */
/* CP-225 Detail follow-up: Leaflet's attribution link (#0078a8) is 3.8:1 on its white chip. Dark theme only,
   so light pixels do not change; #00506b is >= 5.5:1 even over the chip at its worst (80% white on black). */
[data-theme="dark"] .leaflet-control-attribution a {
  color: #00506b;
}

.leaflet-popup-content-wrapper {
  border-radius: 8px;
}

.leaflet-popup-content .day-label {
  color: var(--cs-d-link);
  font-weight: 500;
  font-size: 12px;
  margin-top: 4px;
  display: inline-block;
}
</style>

